import React, { useState, useEffect } from 'react';
import { invoicesAPI } from '../services/api';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { FileText, Eye, PaperPlaneTilt, CurrencyDollar, MagnifyingGlass, CreditCard } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const statusColors = {
  draft: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-400',
  issued: 'status-info',
  sent: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  paid: 'status-success',
  partial: 'status-pending',
  overdue: 'status-error',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
};

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [activeTab, setActiveTab] = useState('invoices');
  
  // Card payments state
  const [cardPayments, setCardPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, [statusFilter]);

  useEffect(() => {
    if (activeTab === 'payments') {
      fetchCardPayments();
    }
  }, [activeTab]);

  const fetchInvoices = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await invoicesAPI.list(params);
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchCardPayments = async () => {
    setPaymentsLoading(true);
    try {
      const [paymentsRes, summaryRes] = await Promise.all([
        api.get('/payments/list'),
        api.get('/payments/summary')
      ]);
      setCardPayments(paymentsRes.data);
      setPaymentSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to load card payments:', error);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const viewInvoice = async (id) => {
    try {
      const response = await invoicesAPI.get(id);
      setSelectedInvoice(response.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load invoice details');
    }
  };

  const handleIssueInvoice = async (id) => {
    try {
      await invoicesAPI.issue(id);
      toast.success('Invoice issued');
      fetchInvoices();
      if (selectedInvoice?.id === id) {
        const response = await invoicesAPI.get(id);
        setSelectedInvoice(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue invoice');
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedInvoice || !paymentAmount) return;
    try {
      await invoicesAPI.recordPayment(selectedInvoice.id, parseFloat(paymentAmount));
      toast.success('Payment recorded');
      setPaymentAmount('');
      const response = await invoicesAPI.get(selectedInvoice.id);
      setSelectedInvoice(response.data);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value || 0);
  };

  const filteredInvoices = invoices.filter(inv => {
    if (!search) return true;
    return (
      inv.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
      inv.customer_name?.toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Invoices & Payments</h1>
          <p className="text-muted-foreground">Manage invoices and card payments</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="invoices" className="gap-2">
            <FileText className="w-4 h-4" />
            Invoices
          </TabsTrigger>
          <TabsTrigger value="payments" className="gap-2">
            <CreditCard className="w-4 h-4" />
            Card Payments
          </TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Total Outstanding</p>
                <p className="text-2xl font-heading font-bold">
                  {formatCurrency(invoices.filter(i => ['issued', 'sent', 'partial'].includes(i.status))
                    .reduce((sum, i) => sum + (i.grand_total - (i.paid_amount || 0)), 0))}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Overdue</p>
                <p className="text-2xl font-heading font-bold text-red-600">
                  {formatCurrency(invoices.filter(i => i.status === 'overdue')
                    .reduce((sum, i) => sum + (i.grand_total - (i.paid_amount || 0)), 0))}
                </p>
              </CardContent>
            </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Draft</p>
            <p className="text-2xl font-heading font-bold">
              {invoices.filter(i => i.status === 'draft').length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Paid This Month</p>
            <p className="text-2xl font-heading font-bold text-emerald-600">
              {formatCurrency(invoices.filter(i => i.status === 'paid')
                .reduce((sum, i) => sum + i.grand_total, 0))}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-sm">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search invoices..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full" />
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No invoices found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.map((invoice) => (
                  <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                    <TableCell className="font-mono text-sm">{invoice.invoice_number}</TableCell>
                    <TableCell>{invoice.customer_name || 'N/A'}</TableCell>
                    <TableCell className="font-mono">{formatCurrency(invoice.grand_total)}</TableCell>
                    <TableCell className="font-mono">{formatCurrency(invoice.paid_amount)}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[invoice.status] || 'bg-slate-100'}>
                        {invoice.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {invoice.due_date ? format(new Date(invoice.due_date), 'dd/MM/yy') : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => viewInvoice(invoice.id)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {invoice.status === 'draft' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleIssueInvoice(invoice.id)}
                            className="text-orange-600"
                            title="Issue invoice"
                          >
                            <PaperPlaneTilt className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Card Payments Tab */}
        <TabsContent value="payments" className="space-y-6">
          {/* Payment Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Total Collected</p>
                <p className="text-2xl font-heading font-bold text-emerald-600">
                  {formatCurrency(paymentSummary?.total_collected || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Today's Collection</p>
                <p className="text-2xl font-heading font-bold text-blue-600">
                  {formatCurrency(paymentSummary?.today_collected || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-heading font-bold text-amber-600">
                  {formatCurrency(paymentSummary?.total_pending || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Paid Count</p>
                <p className="text-2xl font-heading font-bold">
                  {paymentSummary?.paid_count || 0}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Card Payments Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                Card Payments from Driver App
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {paymentsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full" />
                </div>
              ) : cardPayments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No card payments yet</p>
                  <p className="text-sm">Payments taken via the Driver App will appear here</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Driver</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Invoice</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cardPayments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell className="text-sm">
                          {payment.created_at ? format(new Date(payment.created_at), 'dd/MM/yy HH:mm') : '-'}
                        </TableCell>
                        <TableCell>{payment.customer_name || 'N/A'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {payment.driver_name || '-'}
                        </TableCell>
                        <TableCell className="font-mono font-bold">
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            payment.payment_status === 'paid' 
                              ? 'bg-emerald-100 text-emerald-800' 
                              : payment.payment_status === 'pending'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-red-100 text-red-800'
                          }>
                            {payment.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {payment.invoice_id ? (
                            <span className="text-blue-600">{payment.invoice_id.slice(0, 8)}...</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invoice Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Invoice {selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-6 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Customer</p>
                  <p className="font-medium">{selectedInvoice.customer_name}</p>
                  {selectedInvoice.customer_address && (
                    <p className="text-sm text-muted-foreground">{selectedInvoice.customer_address}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge className={statusColors[selectedInvoice.status]}>
                    {selectedInvoice.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Issue Date</p>
                  <p>{selectedInvoice.issued_at 
                    ? format(new Date(selectedInvoice.issued_at), 'dd/MM/yyyy')
                    : 'Not issued'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Due Date</p>
                  <p>{selectedInvoice.due_date 
                    ? format(new Date(selectedInvoice.due_date), 'dd/MM/yyyy')
                    : 'N/A'}</p>
                </div>
              </div>

              {/* Invoice Lines */}
              {selectedInvoice.lines?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Line Items</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Unit Price</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedInvoice.lines.map((line) => (
                          <TableRow key={line.id}>
                            <TableCell>{line.description}</TableCell>
                            <TableCell className="text-right font-mono">{line.quantity}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(line.unit_price)}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(line.line_total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono">{formatCurrency(selectedInvoice.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tax</span>
                  <span className="font-mono">{formatCurrency(selectedInvoice.tax_total)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="font-mono">{formatCurrency(selectedInvoice.grand_total)}</span>
                </div>
                <div className="flex justify-between text-emerald-600">
                  <span>Paid</span>
                  <span className="font-mono">{formatCurrency(selectedInvoice.paid_amount)}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span>Balance Due</span>
                  <span className="font-mono">
                    {formatCurrency(selectedInvoice.grand_total - (selectedInvoice.paid_amount || 0))}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                {selectedInvoice.status === 'draft' && (
                  <Button
                    className="bg-orange-500 hover:bg-orange-400 text-white font-bold"
                    onClick={() => handleIssueInvoice(selectedInvoice.id)}
                  >
                    <PaperPlaneTilt className="w-4 h-4 mr-2" />
                    Issue Invoice
                  </Button>
                )}
                {['issued', 'sent', 'partial'].includes(selectedInvoice.status) && (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      type="number"
                      placeholder="Payment amount"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      className="max-w-[150px]"
                    />
                    <Button onClick={handleRecordPayment}>
                      <CurrencyDollar className="w-4 h-4 mr-2" />
                      Record Payment
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesPage;
